import enviro from 'enviro';
import { useSelector, useDispatch } from 'react-redux';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { useState, Fragment, useEffect, useCallback } from 'react';
import { getCrmConversationId, getOriginalGenericChannelId, isClosing, isOpening } from 'conversations-thread-data/thread-details/public/operators';
import { MARK_THREAD_AS_CLOSED_MARKER } from 'conversations-thread-view/performance-metrics/constants/performanceConstants';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { useTicketStagesForThread, useAssociatedTickets } from 'svh-tickets-lib/tickets/public/hooks';
import { useThreadDetailsWithWootrics } from 'conversations-thread-view/thread-details/hooks/useThreadDetailsWithWootrics';
import { getZorseInbox } from '../../zorse/selectors/getZorseInbox';
import { hasZorseDashboardAccess as hasZorseDashboardAccessSelector } from '../../zorse/selectors/hasZorseDashboardAccess';
import { fetchZorseInboxes } from '../../zorse/actions/fetchZorseInboxes';
// @ts-expect-error type not available yet
import { usesTaxonomyFlow as usesTaxonomyFlowSelector } from 'conversations-thread-view/selectors/conversationSelectors/onCloseSelectors/usesTaxonomyFlow';
// @ts-expect-error type not available yet
import { getThreadStatusChangeInProgress } from 'conversations-thread-view/thread-toolbar/selectors/getThreadStatusChangeInProgress';
// @ts-expect-error type not available yet
import { provideThreadView } from 'conversations-thread-view/thread-view/decorators/provideThreadView';
import MoreActionsDropdown from 'conversations-thread-view/more-actions-dropdown/MoreActionsDropdown';
import ThreadHeaderPrimaryActions from './ThreadHeaderPrimaryActions';
import DeleteThreadPrompt from './DeleteThreadPrompt';
import { useRestoreThread } from '../hooks/useRestoreThread';
import RightSidebarToggle from './RightSidebarToggle';
import { WHATSAPP_GENERIC_CHANNEL_ID } from 'conversations-message-history/common-message-format/constants/genericChannelIds';
import { useTicketPropertyDefinitions } from 'conversations-inbox-lib/tickets/hooks/useTicketPropertyDefinitions';

// https://app.hubspot.com/feedback-editor/53/satisfaction/715/survey
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const WHATS_APP_CSAT_SURVEY = 715;
export const InboxHeaderActionsContainer = ({
  disabled,
  inboxId,
  threadId,
  // TODO: After the new threadToolbar has been fully rolled out, openTaxonomyPanel
  // can be refactored into a hook so we don't need to pass it multiple levels down
  openTaxonomyPanel,
  toggleSidebar,
  isKnownVisitor
}) => {
  const onTriggerWootrics = useCallback(threadDetails => {
    if (getOriginalGenericChannelId(threadDetails) === WHATSAPP_GENERIC_CHANNEL_ID &&
    //@ts-expect-error hsFeedback isn't on the window type but is added in
    window.hsFeedback &&
    //@ts-expect-error hsFeedback isn't on the window type but is added in
    window.hsFeedback.loadSurvey && enviro.isProd()) {
      //@ts-expect-error hsFeedback isn't on the window type but is added in
      window.hsFeedback.loadSurvey('CSAT', WHATS_APP_CSAT_SURVEY);
    }
  }, []);
  const definitionsResults = useTicketPropertyDefinitions(['hs_pipeline', 'hs_pipeline_stage']);
  const {
    asyncThreadDetails,
    closeThread,
    openThread
  } = useThreadDetailsWithWootrics({
    threadId,
    onTriggerWootrics
  });
  const isOpeningThread = isOpening(asyncThreadDetails);
  const isClosingThread = isClosing(asyncThreadDetails);
  const threadDetails = getData(asyncThreadDetails);
  const crmConversationId = getCrmConversationId(threadDetails) || undefined;
  const genericChannelId = getOriginalGenericChannelId(threadDetails);
  const {
    asyncTicketStages
  } = useTicketStagesForThread({
    conversationId: crmConversationId
  });
  const ticketStages = getData(asyncTicketStages);
  const {
    associatedTicket: ticket
  } = useAssociatedTickets({
    conversationId: crmConversationId
  });
  const isThreadStatusChangeInProgress = useSelector(getThreadStatusChangeInProgress);
  const usesTaxonomyFlow = useSelector(state => {
    return usesTaxonomyFlowSelector(state, inboxId);
  });
  const hasZorseDashboardAccess = useSelector(hasZorseDashboardAccessSelector);
  const zorseInboxes = useSelector(getZorseInbox);
  const [showDeleteThreadPrompt, setDeleteThreadPrompt] = useState(false);
  const dispatch = useDispatch();
  useEffect(() => {
    if (hasZorseDashboardAccess && zorseInboxes.isEmpty) {
      dispatch(fetchZorseInboxes());
    }
  }, [dispatch, hasZorseDashboardAccess, zorseInboxes]);
  const handleCloseThread = () => {
    if (usesTaxonomyFlow && openTaxonomyPanel) {
      openTaxonomyPanel();
    } else {
      performance.mark(`${MARK_THREAD_AS_CLOSED_MARKER}-start`);
      closeThread();
      /**
       * TODO: Update tracking for upadtedConversation.
       * The old getUpdatedConversation({ updateType: 'close' }) is missing props
       * and caught by typescript
       */
    }
    // TODO: We should decide whether to continue rendering whatsapp feedback here
  };
  const {
    restoreThread
  } = useRestoreThread({
    ticket,
    threadId,
    /*  @ts-expect-error Jun-7-2024, 15:40UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */
    genericChannelId
  });
  const sharedThreadHeaderProps = {
    disabled,
    closeThread: handleCloseThread,
    isClosingCurrentConversation: isClosingThread,
    restoreThread,
    threadDetails
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(ThreadHeaderPrimaryActions, Object.assign({
      isOpeningThread: isOpeningThread,
      ticketStages: ticketStages,
      isThreadStatusChangeInProgress: isThreadStatusChangeInProgress,
      ticket: ticket,
      openThread: openThread,
      openDeletePrompt: () => setDeleteThreadPrompt(true)
    }, sharedThreadHeaderProps)), /*#__PURE__*/_jsxs(ButtonGroup, {
      display: "flex",
      flexDirection: "row",
      paddingInlineStart: "200",
      children: [/*#__PURE__*/_jsx(MoreActionsDropdown, Object.assign({
        inboxId: inboxId,
        threadId: threadId,
        isKnownVisitor: isKnownVisitor,
        properties: definitionsResults.propertyDefinitions || []
      }, sharedThreadHeaderProps)), /*#__PURE__*/_jsx(RightSidebarToggle, {
        toggleSidebar: toggleSidebar
      })]
    }), /*#__PURE__*/_jsx(DeleteThreadPrompt, {
      showDeleteThreadPrompt: showDeleteThreadPrompt,
      setDeleteThreadPrompt: setDeleteThreadPrompt,
      threadId: threadId,
      ticket: ticket
      /*  @ts-expect-error Jun-7-2024, 15:40UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */,
      originalGenericChannelId: genericChannelId
    })]
  });
};
InboxHeaderActionsContainer.displayName = 'InboxHeaderActionsContainer';
export default provideThreadView(InboxHeaderActionsContainer);